import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { GalleryImage } from '../Gallery/Gallery';
import './galleryLightbox.scss';

interface GalleryLightboxProps {
    images: GalleryImage[];
    initialIndex?: number;
    onClose: () => void;
    onIndexChange?: (index: number) => void;
}

const swipeThreshold = 30; // px

const GalleryLightbox: React.FC<GalleryLightboxProps> = ({ images, initialIndex = 0, onClose, onIndexChange }) => {
    const imgs = useMemo(() => images ?? [], [images]);
    const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(imgs.length - 1, 0)));
    const [mounted, setMounted] = useState(false);
    const touchStartX = useRef<number | null>(null);

    const thumbsScrollRef = useRef<HTMLDivElement | null>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);
    const updateThumbsScrollState = useCallback(() => {
        const el = thumbsScrollRef.current;
        if (!el) {
            setCanScrollUp(false);
            setCanScrollDown(false);
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } = el;
        setCanScrollUp(scrollTop > 0);
        setCanScrollDown(scrollTop + clientHeight < scrollHeight - 1);
    }, []);
    const scrollThumbsBy = useCallback((dir: 1 | -1) => {
        const el = thumbsScrollRef.current;
        if (!el) return;
        const delta = Math.max(120, Math.floor(el.clientHeight * 0.8));
        el.scrollBy({ top: dir * delta, behavior: 'smooth' });
    }, []);

    useEffect(() => setMounted(true), []);

    useLayoutEffect(() => {
        if (!mounted) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollBarWidth = window.innerWidth - html.clientWidth;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (scrollBarWidth) body.style.paddingRight = `${scrollBarWidth}px`;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
        };
        document.addEventListener('keydown', onKey);
        return () => {
            html.style.overflow = '';
            body.style.overflow = '';
            body.style.paddingRight = '';
            document.removeEventListener('keydown', onKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mounted]);

    useEffect(() => {
        setIndex(() => Math.min(Math.max(initialIndex, 0), Math.max(imgs.length - 1, 0)));
    }, [initialIndex, imgs.length]);

    useEffect(() => {
        onIndexChange?.(index);
        const scroller = thumbsScrollRef.current;
        if (!scroller) return;
        const thumb = scroller.querySelector<HTMLElement>(`[data-thumb-index="${index}"]`);
        if (thumb) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        updateThumbsScrollState();
    }, [index, onIndexChange, updateThumbsScrollState]);

    const goNext = useCallback(() => {
        if (imgs.length <= 1) return;
        setIndex((i) => (i + 1 < imgs.length ? i + 1 : 0));
    }, [imgs.length]);

    const goPrev = useCallback(() => {
        if (imgs.length <= 1) return;
        setIndex((i) => (i - 1 >= 0 ? i - 1 : imgs.length - 1));
    }, [imgs.length]);

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        if (dx > swipeThreshold) goPrev();
        else if (dx < -swipeThreshold) goNext();
        touchStartX.current = null;
    };

    // Закрытие по клику на затемнение
    // const onOverlayClick = useCallback(() => onClose(), [onClose]);

    if (!mounted) return null;

    return ReactDOM.createPortal(
        <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label="Просмотр изображений">
            <div
                className="gallery-lightbox__overlay"
                // onClick={onOverlayClick}
                aria-hidden="true"
            />
            <div className="gallery-lightbox__content" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <button className="gallery-lightbox__close" onClick={onClose} type="button" aria-label="Закрыть">
                    <img className="gallery-lightbox__close_img" src="/icons/x.svg" alt="Закрыть" />
                </button>

                {/* Миниатюры слева (desktop) */}
                <aside className="gallery-lightbox__thumbs">
                    <button
                        type="button"
                        className={`gallery-lightbox__thumbs-btn gallery-lightbox__thumbs-btn--up${canScrollUp ? '' : ' is-hidden'}`}
                        onClick={() => scrollThumbsBy(-1)}
                        aria-label="Прокрутить миниатюры вверх"
                    >
                        &#9650;
                    </button>
                    <div className="gallery-lightbox__thumbs-scroll" ref={thumbsScrollRef} onScroll={updateThumbsScrollState}>
                        {imgs.map((img, i) => {
                            const active = i === index;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={`gallery-lightbox__thumb${active ? ' gallery-lightbox__thumb--active' : ''}`}
                                    onClick={() => setIndex(i)}
                                    aria-label={`Показать изображение ${i + 1}`}
                                    aria-current={active ? 'true' : undefined}
                                    data-thumb-index={i}
                                >
                                    <img src={img.src} alt={img.alt ?? `Миниатюра ${i + 1}`} draggable={false} />
                                </button>
                            );
                        })}
                    </div>
                    <button
                        type="button"
                        className={`gallery-lightbox__thumbs-btn gallery-lightbox__thumbs-btn--down${canScrollDown ? '' : ' is-hidden'}`}
                        onClick={() => scrollThumbsBy(1)}
                        aria-label="Прокрутить миниатюры вниз"
                    >
                        &#9660;
                    </button>
                </aside>

                {/* Сцена */}
                <div className="gallery-lightbox__stage">
                    {imgs.length > 1 && (
                        <button
                            type="button"
                            className="gallery-lightbox__arrow gallery-lightbox__arrow--prev"
                            onClick={goPrev}
                            title="Предыдущее фото"
                            aria-label="Предыдущее фото"
                        >
                            &#8249;
                        </button>
                    )}
                    <img
                        className="gallery-lightbox__image"
                        src={imgs[index]?.src}
                        alt={imgs[index]?.alt ?? `Изображение ${index + 1} из ${imgs.length}`}
                        draggable={false}
                    />
                    {imgs.length > 1 && (
                        <button
                            type="button"
                            className="gallery-lightbox__arrow gallery-lightbox__arrow--next"
                            onClick={goNext}
                            title="Следующее фото"
                            aria-label="Следующее фото"
                        >
                            &#8250;
                        </button>
                    )}
                </div>

                {/* Миниатюры снизу (mobile) */}
                <div className="gallery-lightbox__thumbs-bottom" aria-label="Миниатюры">
                    <div className="gallery-lightbox__thumbs-bottom-scroll">
                        {imgs.map((img, i) => {
                            const active = i === index;
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={`gallery-lightbox__thumb gallery-lightbox__thumb--bottom${active ? ' gallery-lightbox__thumb--active' : ''}`}
                                    onClick={() => setIndex(i)}
                                    aria-label={`Показать изображение ${i + 1}`}
                                    aria-current={active ? 'true' : undefined}
                                >
                                    <img src={img.src} alt={img.alt ?? `Миниатюра ${i + 1}`} draggable={false} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default GalleryLightbox;
